import type { BadgeVariant } from '@/components/ui/badge';
import {
  instantOf,
  readSource,
  resolveStatusSource,
  type IReadSource,
} from '@/lib/flights/status-source';
import type {
  AirlineOnlyFlightStatus,
  FlightDataSource,
  FlightStatus,
  IFlightMonitorRow,
} from '@/lib/flights/types';

// Status + risk presentation for the flights monitor (NEX-342 §4). Maps the 5
// flight statuses to Badge variants and derives a coarse risk band from the
// predictor's cancelProbability / expectedDelayMinutes.
//
// i18n: this module is not a component, so it holds translation KEYS, never
// language — `fidsStatus` returns `labelKey`/`labelParams` in the `network`
// namespace and the rendering component calls t(). `FidsStatus.label` survives
// as the English caption only for the call sites outside this route that have
// not migrated yet (see the field's doc comment).

const STATUS_VARIANT: Record<FlightStatus, BadgeVariant> = {
  SCHEDULED: 'neutral',
  DELAYED: 'warning',
  DEPARTED: 'info',
  ARRIVED: 'success',
  CANCELLED: 'error',
};

const STATUS_LABEL: Record<FlightStatus, string> = {
  SCHEDULED: 'Scheduled',
  DELAYED: 'Delayed',
  DEPARTED: 'Departed',
  ARRIVED: 'Arrived',
  CANCELLED: 'Cancelled',
};

/** Normalize an upstream status string (may arrive lowercased/sparse) → one of
 *  the 5 known statuses, or null when unrecognized. */
export function normalizeStatus(raw: string | undefined): FlightStatus | null {
  if (!raw) return null;
  const upper = raw.trim().toUpperCase();
  return upper in STATUS_VARIANT ? (upper as FlightStatus) : null;
}

/**
 * Why this row cannot join a NEW disruption (null → selectable), as a `network`
 * translation KEY the caller renders. Flights already under a disruption/case
 * would only be skipped by the server-side dedup (NEX-288), and an ARRIVED
 * flight has nothing left to disrupt — so the box is disabled up front with the
 * reason as its tooltip. DEPARTED stays selectable: an en-route flight can
 * still be disrupted at its destination.
 *
 * Lives here rather than in `flight-row.tsx` so the board and the row can share
 * it without a non-component export in a component module.
 */
export function selectDisabledReason(row: IFlightMonitorRow): string | null {
  if (row.disruptionUrn) return 'row.disabled.inDisruption';
  if (row.caseUrn) return 'row.disabled.hasCase';
  if (normalizeStatus(row.status) === 'ARRIVED') return 'row.disabled.arrived';
  return null;
}

/**
 * The TODAY board: a live status push that makes a row gone — it left the operator's
 * airport (a `departure` that DEPARTED), or landed (any row that ARRIVED). The BFF
 * decides the board (`today-board.ts`); this only spares the wait for the next read.
 */
export function goneFromBoard(row: IFlightMonitorRow): boolean {
  const status = normalizeStatus(row.status);
  if (!row.boardRole) return false;
  if (status === 'ARRIVED') return true;
  return row.boardRole === 'departure' && status === 'DEPARTED';
}

/** Where «Ahora» falls in a departure-ordered board: the first row leaving at or after `now` (rows.length = none). */
export function nowLineIndex(rows: readonly IFlightMonitorRow[], now: number): number {
  const i = rows.findIndex((r) => {
    const t = r.scheduledDepartureUtc ? Date.parse(r.scheduledDepartureUtc) : NaN;
    return Number.isFinite(t) && t >= now;
  });
  return i < 0 ? rows.length : i;
}

export function statusVariant(status: FlightStatus): BadgeVariant {
  return STATUS_VARIANT[status];
}

export function statusLabel(status: FlightStatus): string {
  return STATUS_LABEL[status];
}

export type RiskBand = 'high' | 'medium' | 'low';

export interface RiskIndicator {
  band: RiskBand;
  variant: BadgeVariant;
  label: string;
}

/**
 * Derive a risk indicator from the predictor signals. cancelProbability is the
 * primary driver (≥0.4 high, ≥0.2 medium); a large expected delay can lift a
 * low-cancel flight to medium. Returns null when we have no signal at all (the
 * row simply shows no risk chip rather than a misleading "low").
 */
export function riskIndicator(
  cancelProbability?: number,
  expectedDelayMinutes?: number,
): RiskIndicator | null {
  const hasCancel = typeof cancelProbability === 'number';
  const hasDelay = typeof expectedDelayMinutes === 'number';
  if (!hasCancel && !hasDelay) return null;

  const cancel = hasCancel ? cancelProbability! : 0;
  const delay = hasDelay ? expectedDelayMinutes! : 0;

  let band: RiskBand = 'low';
  if (cancel >= 0.4 || delay >= 120) band = 'high';
  else if (cancel >= 0.2 || delay >= 45) band = 'medium';

  const variant: BadgeVariant = band === 'high' ? 'error' : band === 'medium' ? 'warning' : 'muted';
  const pct = hasCancel ? `${Math.round(cancel * 100)}% cancel` : undefined;
  const dly = hasDelay && delay > 0 ? `${delay}m delay` : undefined;
  const detail = [pct, dly].filter(Boolean).join(' · ');
  const label = detail || (band === 'low' ? 'Low risk' : `${band} risk`);

  return { band, variant, label };
}

/* ── FIDS board presentation (departures-board restyle) ─────────────────────── */

/** What the board's STATUS cell shows — label + tone + whether the row dims. */
export interface FidsStatus {
  /**
   * Uppercase FIDS caption in ENGLISH — the pre-i18n field, kept for the
   * surfaces outside this route that still render it directly (express,
   * passenger search). New call sites render `labelKey` instead.
   * @deprecated render `t(labelKey, labelParams)` with the `network` namespace.
   */
  label: string;
  /**
   * `network` translation key for the caption (this file holds keys, never language).
   * `boardStatusCell`'s airline-only cells carry a namespace-qualified key instead.
   */
  labelKey: string;
  /** Interpolation for `labelKey` (DELAYED carries the minutes of the status's source). */
  labelParams?: { minutes: number };
  /** Token-native text color (same semantic hues as the rest of the console). */
  textClass: string;
  /** Terminal rows (arrived/cancelled) read as history — the board dims them. */
  dim: boolean;
}

const FIDS: Record<
  FlightStatus,
  Omit<FidsStatus, 'label' | 'labelKey' | 'labelParams'> & { caption: string }
> = {
  SCHEDULED: { caption: 'ON TIME', textClass: 'text-emerald-600 dark:text-emerald-400', dim: false },
  DELAYED: { caption: 'DELAYED', textClass: 'text-amber-600 dark:text-amber-400', dim: false },
  DEPARTED: { caption: 'DEPARTED', textClass: 'text-sky-600 dark:text-sky-400', dim: false },
  ARRIVED: { caption: 'ARRIVED', textClass: 'text-muted-foreground', dim: true },
  CANCELLED: { caption: 'CANCELLED', textClass: 'text-rose-600 dark:text-rose-400', dim: true },
};

/** Status cell for one row; unknown/absent status renders an em-dash cell. */
export function fidsStatus(
  status: FlightStatus | null,
  expectedDelayMinutes?: number,
): FidsStatus {
  if (!status) {
    return {
      label: '—',
      labelKey: 'fids.unknown',
      textClass: 'text-muted-foreground',
      dim: false,
    };
  }
  const base = FIDS[status];
  const minutes =
    status === 'DELAYED' && typeof expectedDelayMinutes === 'number' && expectedDelayMinutes > 0
      ? Math.round(expectedDelayMinutes)
      : null;
  return {
    label: minutes === null ? base.caption : `${base.caption} +${minutes}m`,
    ...(minutes === null
      ? { labelKey: `fids.${status}` }
      : { labelKey: 'fids.delayedWithMinutes', labelParams: { minutes } }),
    textClass: base.textClass,
    dim: base.dim,
  };
}

/* ── Source of the status and the risk (providers-by-service §8b) ──────────── */

// The BFF stamps per-field provenance on each row (`provenance.status`) and a
// `riskSource` next to the predictor fields. This resolver only READS them —
// provenance is data, never recomputed here (§6 inv. 6). It returns `network`
// keys, never language; an absent or unknown source resolves to null so an
// older BFF renders no badge rather than a guessed one.

/** One field's source, ready to render. */
export interface FlightSourceHint {
  source: FlightDataSource;
  /** `network` translation key for the source name (`row.source.<SOURCE>`). */
  labelKey: string;
  /** When that source observed the value (ISO-8601 UTC), if it said. */
  observedAt?: string;
}

/** The two sources the board shows per row; null where the row carries none. */
export interface FlightRowSources {
  status: FlightSourceHint | null;
  risk: FlightSourceHint | null;
}

function hintOf(read: IReadSource | null): FlightSourceHint | null {
  return read ? { ...read, labelKey: `row.source.${read.source}` } : null;
}

/** The status's source (synthetic-airline §8b) — the winner of load and live, see `resolveStatusSource`. */
function statusSourceHint(row: IFlightMonitorRow): FlightSourceHint | null {
  return hintOf(resolveStatusSource(row));
}

/**
 * Status source = whichever of `provenance.status` and the live top-level
 * `statusSource` wins the status — the airline first, then the newer (see
 * `resolveStatusSource`). Risk source = `riskSource`, and only
 * while the row still carries a risk signal: no forecast means an empty risk
 * cell, so there is nothing to attribute (never a "0%" with a badge).
 */
export function flightRowSources(row: IFlightMonitorRow): FlightRowSources {
  const hasRisk =
    typeof row.cancelProbability === 'number' || typeof row.expectedDelayMinutes === 'number';
  return {
    status: statusSourceHint(row),
    risk: hasRisk ? hintOf(readSource(row.riskSource, row.riskObservedAt)) : null,
  };
}

/**
 * Minutes the STATUS cell may append to DELAYED (`+Nm`) — always the minutes of
 * the source that reported the status, so one caption never mixes two sources:
 * - the airline → its own `delayMinutes` (ETD − its STD), never the forecast;
 * - the predictor → its forecast `expectedDelayMinutes`, as does a row with no
 *   source at all and no provenance (an older BFF, today's behavior);
 * - a file or synthetic status, or provenance that says nothing of the status → none.
 */
export function statusCaptionMinutes(row: IFlightMonitorRow): number | undefined {
  const source = statusSourceHint(row)?.source;
  if (source === 'SERVICE_PROVIDER') return row.delayMinutes;
  if (source === 'PREDICTOR') return row.expectedDelayMinutes;
  return source === undefined && row.provenance === undefined
    ? row.expectedDelayMinutes
    : undefined;
}

/**
 * The airline's estimated departure (ISO-8601 UTC) to show in the STATUS cell —
 * only while the status it explains is the airline's DELAYED. A live delta leaves
 * absent fields out, so after «back on schedule», a cancellation or a predictor
 * status an old ETD can still sit on the cached row: it must not show. Null
 * otherwise, and for an instant that does not parse.
 */
export function airlineEtd(row: IFlightMonitorRow): string | null {
  if (normalizeStatus(row.status) !== 'DELAYED') return null;
  if (statusSourceHint(row)?.source !== 'SERVICE_PROVIDER') return null;
  const etd = row.estimatedDepartureUtc;
  return etd && instantOf(etd) !== null ? etd : null;
}

/**
 * Statuses only the airline reports, outside the FIDS five. DIVERTED has no
 * `network` key and no FIDS caption: it reuses the delay badge's existing
 * `disruptions:delay.badge.kind.DIVERTED` («Desviado» — the cell uppercases it),
 * namespace-qualified, in the delayed tone (an exception still in play, not history).
 */
const AIRLINE_ONLY_CELL: Record<AirlineOnlyFlightStatus, FidsStatus> = {
  DIVERTED: {
    label: 'DIVERTED',
    labelKey: 'disruptions:delay.badge.kind.DIVERTED',
    textClass: FIDS.DELAYED.textClass,
    dim: false,
  },
};

/**
 * The STATUS cell of a board row: the FIDS five through `fidsStatus`, with the
 * minutes of the status's own source (`statusCaptionMinutes`), plus the airline's
 * DIVERTED. Kept apart from `fidsStatus`, whose other call sites prefix its key
 * with `network:` and would break on a namespace-qualified one.
 */
export function boardStatusCell(row: IFlightMonitorRow): FidsStatus {
  const upper = row.status?.trim().toUpperCase();
  if (upper && upper in AIRLINE_ONLY_CELL) {
    return AIRLINE_ONLY_CELL[upper as AirlineOnlyFlightStatus];
  }
  return fidsStatus(normalizeStatus(row.status), statusCaptionMinutes(row));
}

/** Compact RISK cell (the predictor % a real FIDS doesn't have). Null → no signal. */
export function riskCell(
  cancelProbability?: number,
  expectedDelayMinutes?: number,
): { text: string; textClass: string } | null {
  const risk = riskIndicator(cancelProbability, expectedDelayMinutes);
  if (!risk) return null;
  const text =
    typeof cancelProbability === 'number'
      ? `${Math.round(cancelProbability * 100)}%`
      : `+${Math.round(expectedDelayMinutes ?? 0)}m`;
  const textClass =
    risk.band === 'high'
      ? 'font-semibold text-rose-600 dark:text-rose-400'
      : risk.band === 'medium'
        ? 'text-amber-600 dark:text-amber-400'
        : 'text-muted-foreground';
  return { text, textClass };
}

/** Fold for the board's summary strip (over the loaded rows). */
export interface BoardSummary {
  onTime: number;
  delayed: number;
  departed: number;
  arrived: number;
  cancelled: number;
  /** High-band predictor risk only — the sharp "look here" count. */
  atRisk: number;
}

export function summarizeBoard(
  rows: { status?: string; cancelProbability?: number; expectedDelayMinutes?: number }[],
): BoardSummary {
  const s: BoardSummary = { onTime: 0, delayed: 0, departed: 0, arrived: 0, cancelled: 0, atRisk: 0 };
  for (const row of rows) {
    switch (normalizeStatus(row.status)) {
      case 'SCHEDULED': s.onTime += 1; break;
      case 'DELAYED': s.delayed += 1; break;
      case 'DEPARTED': s.departed += 1; break;
      case 'ARRIVED': s.arrived += 1; break;
      case 'CANCELLED': s.cancelled += 1; break;
      default: break;
    }
    if (riskIndicator(row.cancelProbability, row.expectedDelayMinutes)?.band === 'high') {
      s.atRisk += 1;
    }
  }
  return s;
}
